<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO, isValid, isPast } from 'date-fns';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: { type: Number, required: true },
  title: { type: String, default: '' },
  taskType: { type: String, default: 'other' },
  status: { type: String, default: 'pending' },
  priority: { type: String, default: 'medium' },
  dueDate: { type: String, default: '' },
  userName: { type: String, default: '' },
  taskableType: { type: String, default: '' },
  taskableName: { type: String, default: '' },
  updatedAt: { type: [String, Number], default: null },
});

const emit = defineEmits(['showTask', 'complete']);

const { t } = useI18n();

const onClickViewDetails = () => emit('showTask', props.id);
const onClickComplete = () => emit('complete', props.id);

const displayTitle = computed(() => props.title || t('TASKS.UNTITLED'));

const statusColor = computed(() => {
  const colors = {
    pending: 'bg-n-slate-9/10 text-n-slate-11',
    in_progress: 'bg-n-blue-9/10 text-n-blue-11',
    completed: 'bg-n-teal-9/10 text-n-teal-11',
    cancelled: 'bg-n-ruby-9/10 text-n-ruby-11',
  };
  return colors[props.status] || colors.pending;
});

const priorityColor = computed(() => {
  const colors = {
    low: 'text-n-slate-11',
    medium: 'text-n-amber-11',
    high: 'text-n-ruby-11',
  };
  return colors[props.priority] || colors.medium;
});

const priorityIcon = computed(() => {
  const icons = {
    low: 'i-lucide-arrow-down',
    medium: 'i-lucide-minus',
    high: 'i-lucide-arrow-up',
  };
  return icons[props.priority] || icons.medium;
});

const taskTypeIcon = computed(() => {
  const icons = {
    call: 'i-lucide-phone',
    email: 'i-lucide-mail',
    meeting: 'i-lucide-users',
    follow_up: 'i-lucide-repeat',
    other: 'i-lucide-check-square',
  };
  return icons[props.taskType] || icons.other;
});

const isOverdue = computed(() => {
  if (!props.dueDate || props.status === 'completed' || props.status === 'cancelled') return false;
  try {
    const date = typeof props.dueDate === 'string' ? parseISO(props.dueDate) : new Date(props.dueDate);
    return isValid(date) && isPast(date);
  } catch (e) {
    return false;
  }
});

const formattedDueDate = computed(() => {
  if (!props.dueDate) return '';
  try {
    const date = typeof props.dueDate === 'string' ? parseISO(props.dueDate) : new Date(props.dueDate);
    if (!isValid(date)) return '';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return '';
  }
});
</script>

<template>
  <CardLayout
    layout="row"
    class="cursor-pointer transition-all duration-200 hover:outline-n-brand hover:shadow-md group/cardLayout"
    :class="{ 'opacity-60': status === 'completed' || status === 'cancelled' }"
    @click="onClickViewDetails"
  >
    <div class="flex items-center justify-start flex-1 gap-4">
      <div class="flex items-center justify-center w-12 h-12 rounded-full bg-n-alpha-2 shrink-0">
        <Icon :icon="taskTypeIcon" size="size-5 text-n-slate-11" />
      </div>
      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
          <span class="text-base font-medium truncate text-n-slate-12" :class="{ 'line-through': status === 'completed' }">
            {{ displayTitle }}
          </span>
          <span
            :class="['px-2 py-0.5 rounded-full text-xs font-medium', statusColor]"
          >
            {{ $t(`TASKS.STATUS.${status.toUpperCase()}`) }}
          </span>
          <span v-if="isOverdue" class="px-2 py-0.5 rounded-full text-xs font-medium bg-n-ruby-9/10 text-n-ruby-11">
            {{ $t('TASKS.OVERDUE') }}
          </span>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
            <span :class="['inline-flex items-center gap-1.5 text-sm truncate', priorityColor]">
              <Icon :icon="priorityIcon" size="size-3.5" />
              <span class="capitalize">{{ priority }}</span>
            </span>
            <div v-if="taskableName" class="w-px h-3 bg-n-slate-6" />
            <span v-if="taskableName" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-briefcase" size="size-3.5 text-n-slate-11" />
              {{ taskableName }}
            </span>
            <div v-if="userName" class="w-px h-3 bg-n-slate-6" />
            <span v-if="userName" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-user" size="size-3.5 text-n-slate-11" />
              {{ userName }}
            </span>
            <div v-if="dueDate" class="w-px h-3 bg-n-slate-6" />
            <span v-if="dueDate" class="inline-flex items-center gap-1.5 text-sm text-n-slate-11 truncate">
              <Icon icon="i-lucide-calendar" size="size-3.5" :class="isOverdue ? 'text-n-ruby-11' : 'text-n-slate-11'" />
              <span :class="isOverdue ? 'text-n-ruby-11 font-medium' : ''">{{ formattedDueDate }}</span>
            </span>
          </div>
          <div class="flex items-center gap-3">
            <Button
              v-if="status !== 'completed' && status !== 'cancelled'"
              :label="$t('TASKS.COMPLETE')" 
              
              variant="ghost"
              size="xs"
              class="opacity-0 group-hover/cardLayout:opacity-100 transition-opacity"
              @click.stop="onClickComplete"
            />
            <Button
              :label="$t('TASKS.VIEW_DETAILS')"
              variant="link"
              size="xs"
              class="opacity-0 group-hover/cardLayout:opacity-100 transition-opacity"
              @click.stop="onClickViewDetails"
            />
          </div>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
